import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { useGetChatRooms } from '@pocapan/shared/domains/chat/ui/hooks/useGetChatRooms';
import useRefreshJwtQuery from '../../components/api/queries/useRefreshJwtQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import { FEATURE_KEYS } from '@pocapan/shared/domains/features/constants';
import FeatureGate from '@pocapan/shared/domains/features/ui/components/FeatureGate';
import HomeBottomNav from '../components/bar/HomeBottomNav';
import { getLanguage } from '@pocapan/shared/utils/etcUtils';
import saveData from '@pocapan/shared/utils/saveData';
import useArtistQueryLikedOnly from '../hooks/queries/useArtistQueryLikedOnly';
import HorizontalScroll from '../my-page/components/HorizontalScroll';
import NotificationBadge from '../notifications/components/NotificationBadge';
import NavigateArtistSelectButton from '../select-artist/NavigateArtistSelectButton';
import ChatItem from './components/ChatItem';
import ChatItemListSkeleton from './components/ChatItemListSkeleton';
import { usePushNotification } from './hooks/usePushNotification';
import EmptyCase from '../../components/errors/EmptyCase';
import type { InfiniteData } from 'react-query';
import type { components } from '@pocapan/shared/types/schema';
import type { GetChatRoomsListResponse } from '@pocapan/shared/domains/chat/services/chatService';

type ChatRoom = components['schemas']['ChatRoom'];
type InfiniteChatRoomsData = InfiniteData<GetChatRoomsListResponse>;

const ALL_MESSAGE_CATEGORIES = ['All', 'DM', 'Trading', 'Exchanging'];

const ChatMainPage = () => {
  const { i18n } = useTranslation();

  const [chatRoomsCategory, setChatRoomsCategory] = useState('All');
  const [paginatedChatRooms, setPaginatedChatRooms] = useState<ChatRoom[]>();
  const [isRequestArtistSelectPopupOpen, setIsRequestArtistSelectPopupOpen] = useState(false);

  const { colorMode } = useColorMode();

  const { data, isFetching, fetchNextPage, hasNextPage } = useGetChatRooms({
    type: chatRoomsCategory as 'All' | 'DM' | 'Trading' | 'Exchanging',
    onlyJoined: true
  });

  const { data: likedArtists, isLoading: isLoadingLikedArtists } = useArtistQueryLikedOnly();
  usePushNotification(undefined, 'chatMain');

  const navigate = useNavigate();
  const user = useUserQuery();

  useEffect(() => {
    if (isLoadingLikedArtists) return;
    if (likedArtists && likedArtists.length === 0) {
      setIsRequestArtistSelectPopupOpen(true);
    } else {
      setIsRequestArtistSelectPopupOpen(false);
    }
  }, [likedArtists, isLoadingLikedArtists]);

  const authToken = useRefreshJwtQuery();
  if (authToken?.jwt) {
    saveData('token', JSON.stringify(authToken?.jwt));
  }

  useEffect(() => {
    if (user) {
      saveData('userId', user?.userId);
      saveData('email', user?.email);
    }
    if (user && !isEmpty(user.locale) && getLanguage() !== user.locale) {
      saveData('i18nextLng', user.locale);
      i18n.changeLanguage(user.locale);
    }
  }, [user, i18n]);

  useEffect(() => {
    localStorage.removeItem('latestPostId');
  }, []);

  const handlePaginatedChatRooms = useCallback((paginatedData: InfiniteChatRoomsData) => {
    const chatRooms = paginatedData.pages?.flatMap((page) => page.chatRooms) ?? [];
    const sorted = [...chatRooms].sort((a, b) => {
      const aTime = a?.lastMessage?.sentAt ? new Date(a.lastMessage.sentAt).getTime() : 0;
      const bTime = b?.lastMessage?.sentAt ? new Date(b.lastMessage.sentAt).getTime() : 0;
      return bTime - aTime;
    });
    setPaginatedChatRooms(sorted);
  }, []);

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleNavigateArtistSelectButton = () => {
    if (user.baseUserProfile?.nickname) {
      navigate('/search-artists', { replace: true });
    } else {
      navigate('/nickname-setting?init=true', { replace: true });
    }
    setIsRequestArtistSelectPopupOpen(false);
  };

  useEffect(() => {
    if (data && !isEmpty(data)) {
      handlePaginatedChatRooms(data);
    }
  }, [data, handlePaginatedChatRooms]);

  const isLikeSpecificArtist = (artistId: string) => {
    if (!likedArtists) return false;
    return likedArtists.some((artist) => artist.artistId === artistId);
  };

  return (
    <div className="min-h-screen pt-[calc(env(safe-area-inset-top))] pb-[calc(120px+env(safe-area-inset-bottom))] bg-background">
      <Helmet>
        <title>{t('tabs.chat')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <header className="flex items-center justify-between px-[24px] py-[11px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px]">
          {t('ChatPage.title')}
        </h1>
        <NotificationBadge />
      </header>
      <FeatureGate featureKey={FEATURE_KEYS.CARD_REVIEW} inverted>
        <HorizontalScroll>
          <div className="flex gap-[8px] px-[20px] py-[8px] mb-[8px]">
            {ALL_MESSAGE_CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => {
                  setPaginatedChatRooms(undefined);
                  setChatRoomsCategory(category);
                }}
                className={`rounded-[32px] px-[12px] py-[6px] text-[13px] font-semibold leading-none border transition-colors
                ${
                  category === chatRoomsCategory
                    ? 'bg-text-main-lv2 text-pure-white dark:text-pure-black border-transparent'
                    : 'bg-on-primary dark:bg-transparent text-text-main-lv2 border-separator2'
                }
              `}
              >
                {t(`notifications.messageCategory${category}`)}
              </button>
            ))}
          </div>
        </HorizontalScroll>
      </FeatureGate>

      {/* 메시지 목록 */}
      <div className="pb-[24px]">
        {paginatedChatRooms === undefined ? (
          <ChatItemListSkeleton />
        ) : (
          <>
            <FeatureGate featureKey={FEATURE_KEYS.CARD_REVIEW} inverted>
              {chatRoomsCategory === 'All' && (
                <>
                  <ChatItem variant="lounge" categoryGroup="community" />
                  {isLikeSpecificArtist('nctwish') && (
                    <ChatItem variant="lounge" categoryGroup="nctwish" />
                  )}
                </>
              )}
            </FeatureGate>
            {paginatedChatRooms.length > 0 ? (
              <Virtuoso
                useWindowScroll
                overscan={480}
                defaultItemHeight={72}
                style={{ minHeight: 100 }}
                data={paginatedChatRooms ?? []}
                endReached={loadMore}
                itemContent={(_index, chatRoom: ChatRoom) => (
                  <ChatItem variant="chat" chatRoom={chatRoom} />
                )}
              />
            ) : chatRoomsCategory === 'All' ? null : (
              // @ts-expect-error
              <EmptyCase description={t('ChatPage.emptyChatRooms')} />
            )}
          </>
        )}
      </div>
      {isRequestArtistSelectPopupOpen && (
        <RequestArtistSelectPopup onClick={handleNavigateArtistSelectButton} />
      )}
      <HomeBottomNav tabIndex={3} />
    </div>
  );
};

export default ChatMainPage;

const RequestArtistSelectPopup = ({ onClick }: { onClick: () => void }) => {
  const { colorMode } = useColorMode();
  return (
    <div
      className="min-h-screen fixed inset-0 z-[1000] flex flex-col items-center justify-center"
      style={{
        backgroundImage: `url(/assets/force_to_update/bg_ask_update_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="flex flex-col items-center justify-center gap-[11px]">
        <img
          src="/assets/icons/collections/Milky Way.png"
          alt="milky way"
          className="w-[160px] h-[160px]"
        />
        <div className="flex flex-col px-[24px] py-[10px] gap-[14px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] text-center">
            {t('requestArtistSelectPopup.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] text-center whitespace-pre-line">
            {t('requestArtistSelectPopup.description')}
          </p>
        </div>
      </div>
      <div className="mt-[26px]">
        <NavigateArtistSelectButton onClick={onClick} />
      </div>
    </div>
  );
};
